import React, {useCallback, useState} from 'react';

import {PermissionsTable} from 'src/admin-rights-service/components/permissions-table';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {ACCESS_RIGHTS_LABELS} from 'src/hooks/access-rights/constants';
import {disableAccessRights, enableAccessRights} from 'src/openapi/admin/apis/AdminBulkUpdateAccessApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Scroll} from '@pb/kit-component-scroll';
import {Pill} from '@pb/kit-component-strip-of-pills';
import {pluralize} from '@pb/kit-formatters';

import {DISABLE_ACCESS_RIGHTS, ENABLE_ACCESS_RIGHTS} from './constants';

import {permissionsModal} from './permissions-modal.module.styl';


type Props = {
    enable: boolean,
    onCloseModal: (submit: boolean) => void,
    userIds: number[],
};

export const PermissionsModal = ({userIds, onCloseModal, enable}: Props) => {
    const apiDispatch = useAppDispatch();

    const [enabledPermissions, setEnabledPermissions] = useState<string[]>(
        enable ? [] : Object.values(ACCESS_RIGHTS),
    );
    const createBackgroundTask = useBackgroundTaskManager();

    const updatePermissions = useCallback(async () => {
        onCloseModal(true);
        const backgroundTask = createBackgroundTask(
            'Update permissions',
            <div style={{paddingRight: 16}}>
                Updating permissions for <b>{userIds.length}</b> {pluralize('user[s]', userIds.length)} in progress
            </div>,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (backgroundTask) {
            const action = enable ? enableAccessRights : disableAccessRights;
            const rightsCodes = enable
                ? enabledPermissions
                : Object.values(ACCESS_RIGHTS).filter(right => !enabledPermissions.includes(right));

            const updatePermissionsResponse = await apiDispatch(action({
                updateAccessRequest: {
                    rightsCodes,
                    userIds,
                },
            }));

            if (updatePermissionsResponse.payload.success) {
                backgroundTask.completeSuccess(
                    <div style={{paddingRight: 16}}>
                        <b>{pluralize('Permission[s] successfully updated', enabledPermissions.length)}</b>
                    </div>,
                );
            } else {
                backgroundTask.completeFailure(
                    <div style={{paddingRight: 16}}>
                        <b>{
                            getError({
                                error: updatePermissionsResponse.payload.data,
                                separator: ' ',
                            })
                        }
                        </b>
                    </div>,
                );
            }
        }
    }, [enabledPermissions, onCloseModal, userIds, enable, apiDispatch, createBackgroundTask]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {enable ? ENABLE_ACCESS_RIGHTS : DISABLE_ACCESS_RIGHTS}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Scroll>
                    <div className={permissionsModal} style={{paddingTop: 0}}>
                        <div>
                            <Label>{`${enable ? 'Granted' : 'Restricted'} permissions`}</Label>
                            <ResponsiveElementGroup padding="0 8px 4px 0">
                                {(enable
                                    ? enabledPermissions
                                    : Object.values(ACCESS_RIGHTS)
                                            .filter(right => !enabledPermissions.includes(right))
                                ).map(permission => (
                                    <Pill
                                        key={permission}
                                        name={ACCESS_RIGHTS_LABELS[
                                            permission as keyof typeof ACCESS_RIGHTS_LABELS
                                        ]}
                                        variant={enable ? 'ADDITION' : 'EXCLUSION'}
                                    />
                                ))}
                            </ResponsiveElementGroup>
                        </div>
                        <PermissionsTable permissions={enabledPermissions} setPermissions={setEnabledPermissions}/>
                    </div>
                </Scroll>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        disabled={!(enable
                            ? enabledPermissions
                            : Object.values(ACCESS_RIGHTS)
                                    .filter(right => !enabledPermissions.includes(right))).length}
                        onClick={updatePermissions}>
                        Update
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
